import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { formatIDR, formatDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import MemberLayout from '../components/MemberLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { StatusBadge } from '../components/StatusBadge';
import { RiskAlert } from '../components/RiskAlert';
import { GraduationCap, Landmark, Users, Wallet, ArrowRight, Lock } from 'lucide-react';

export default function Dashboard() {
  const { user, membership, isActive } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [practice, setPractice] = useState(null);
  const [network, setNetwork] = useState(null);

  useEffect(() => {
    if (!isActive) return;
    api.get('/academy/progress').then(r => setProgress(r.data.progress)).catch(()=>{});
    api.get('/practice/account').then(r => setPractice(r.data)).catch(()=>{});
    api.get('/referral/network').then(r => setNetwork(r.data)).catch(()=>{});
  }, [isActive]);

  return (
    <MemberLayout>
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Selamat datang, {user?.name?.split(' ')[0]}</h1>
        <div className="flex items-center gap-3 mt-2">
          <StatusBadge status={membership?.status || 'PENDING'} testid="membership-status-badge" />
          {membership?.membership_expiry_date && <span className="text-sm text-muted-foreground">Berlaku hingga {formatDate(membership.membership_expiry_date)}</span>}
        </div>
      </div>

      {!isActive && (
        <Card className="p-6 mb-6 border-[hsl(var(--brand-gold)/0.4)] bg-[hsl(var(--brand-gold)/0.06)]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3"><Lock className="h-5 w-5 text-amber-600 mt-0.5" /><div><h3 className="font-semibold">Membership belum aktif</h3><p className="text-sm text-muted-foreground">Selesaikan pembayaran & verifikasi untuk membuka akses Academy.</p></div></div>
            <Button className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" onClick={() => navigate('/membership')} data-testid="activate-membership-button">Aktifkan Membership</Button>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={GraduationCap} label="Academy Progress" value={`${progress?.percent ?? 0}%`} sub={`${progress?.completed_lessons ?? 0}/${progress?.total_lessons ?? 0} lesson`} />
        <StatCard icon={Landmark} label="Practice Balance" value={formatIDR(practice?.current_balance || 0)} sub={`Alokasi ${formatIDR(practice?.monthly_allocation || 0)}/bln`} />
        <StatCard icon={Users} label="Referral Commission" value={formatIDR(network?.commission_balance || 0)} sub={`Maks ${formatIDR(network?.max_monthly || 56250)}/bln`} />
        <StatCard icon={Wallet} label="My Network" value={`${(network?.levels?.L1?.count||0)+(network?.levels?.L2?.count||0)+(network?.levels?.L3?.count||0)+(network?.levels?.L4?.count||0)}`} sub={`L1:${network?.levels?.L1?.count||0} L2:${network?.levels?.L2?.count||0} L3:${network?.levels?.L3?.count||0} L4:${network?.levels?.L4?.count||0}`} />
      </div>

      {isActive && (
        <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}>
          <Card className="p-6 mt-6">
            <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Gold Trading Academy</h3><span className="text-sm text-muted-foreground">Quiz score {progress?.quiz_score ?? 0}%</span></div>
            <Progress value={progress?.percent ?? 0} className="h-3" />
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">{progress?.completed_lessons ?? 0} dari {progress?.total_lessons ?? 0} lesson selesai</span>
              <Button onClick={() => navigate('/academy')} data-testid="continue-learning-button">Continue Learning <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </Card>
        </motion.div>
      )}

      <div className="mt-6"><RiskAlert /></div>
    </MemberLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon className="h-4 w-4" />{label}</div>
      <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </Card>
  );
}
